import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import CrossIcon from '@atlaskit/icon/core/cross';
import { IconButton } from '@atlaskit/button/new';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import { KeepPageStructureButton } from './KeepPageStructureButton.tsx';

const i18n = defineMessages({
	titlePage: {
		id: 'experiment-copy-with-rovo.whats-next-card.title-page',
		defaultMessage: 'Make this copied page your own',
		description: 'Title for the banner card when a regular page was duplicated',
	},
	titleLiveDoc: {
		id: 'experiment-copy-with-rovo.whats-next-card.title-live-doc',
		defaultMessage: 'Make this copied live doc your own',
		description: 'Title for the banner card when a live doc was duplicated',
	},
	description: {
		id: 'experiment-copy-with-rovo.whats-next-card.description',
		defaultMessage:
			'Ask Rovo to remove text and images, but keep important structures like headers or tables.',
		description:
			'Description text explaining how Rovo AI can strip content while preserving page structure',
	},
	closeLabel: {
		id: 'experiment-copy-with-rovo.whats-next-card.close',
		defaultMessage: 'Dismiss',
		description: 'Accessible label for the close button on the Whats Next card',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
	},
	cardContainer: {
		maxWidth: '760px',
		width: '100%',
		boxSizing: 'border-box',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		backgroundColor: token('elevation.surface'),
	},
	title: {
		flexGrow: 1,
	},
});

export type CopyWithRovoBannerCardProps = {
	/**
	 * The content ID of the page. Used by the KeepPageStructureButton to target the correct page.
	 */
	contentId?: string;
};

export const CopyWithRovoBannerCard = ({ contentId }: CopyWithRovoBannerCardProps) => {
	const { formatMessage } = useIntl();
	const isLivePage = useIsCurrentPageLive();
	const [isDismissed, setIsDismissed] = useState(false);

	const handleDismiss = useCallback(() => {
		setIsDismissed(true);
	}, []);

	if (isDismissed) {
		return null;
	}

	return (
		<div css={styles.wrapper} data-testid="whats-next-card-wrapper">
			<div css={styles.cardContainer} data-testid="whats-next-card">
				<Stack space="space.100">
					<Inline spread="space-between">
						<div css={styles.title}>
							<Text as="span" weight="bold" size="large">
								{formatMessage(isLivePage ? i18n.titleLiveDoc : i18n.titlePage)}
							</Text>
						</div>
						<IconButton
							appearance="subtle"
							icon={CrossIcon}
							label={formatMessage(i18n.closeLabel)}
							onClick={handleDismiss}
							testId="whats-next-card-close"
						/>
					</Inline>
					<Text as="p" color="color.text.subtle">
						{formatMessage(i18n.description)}
					</Text>
					<Inline space="space.100" alignBlock="center">
						<KeepPageStructureButton contentId={contentId} />
					</Inline>
				</Stack>
			</div>
		</div>
	);
};
